import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  PendingTranscription,
  loadPendingTranscriptions,
  savePendingTranscriptions,
} from '../../lib/pendingTranscriptions';

/**
 * Jobs held back until the transcription session resets.
 *
 * No `api.status`: nothing here is a request. The queue is entirely client-side — the server never
 * accepted these jobs, which is why they are queued — so the shape is just the list.
 *
 * `pendingTranscriptions.container` owns the release; this slice only holds the list and keeps
 * localStorage in step with it.
 */

interface PendingTranscriptionsState {
  data: PendingTranscription[];
}

const initialState: PendingTranscriptionsState = {
  // Seeded from localStorage so a job queued before a reload is still queued after one. The store
  // is reset on a session change, and `lib/pendingTranscriptions` drops the key at the same time,
  // so this cannot resurrect the previous account's queue.
  data: loadPendingTranscriptions(),
};

const pendingTranscriptionsSlice = createSlice({
  name: 'pendingTranscriptions',
  initialState,
  reducers: {
    /**
     * The write to localStorage happens here rather than in the caller, deliberately: the queue's
     * whole value is surviving a reload, and a persistence step that each call site had to remember
     * would eventually be forgotten by one of them. It is a synchronous write derived purely from
     * the state being returned.
     */
    enqueuePendingTranscription: (state, action: PayloadAction<PendingTranscription>) => {
      // One queued job per audio file. Asking twice replaces rather than stacks — two identical
      // jobs would both fire on release and spend the budget twice for one result.
      state.data = [
        ...state.data.filter(entry => entry.audioId !== action.payload.audioId),
        action.payload,
      ];
      savePendingTranscriptions(state.data);
    },

    removePendingTranscription: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter(entry => entry.id !== action.payload);
      savePendingTranscriptions(state.data);
    },

    /** The session was still full on release — push the entry out to the new reset instant. */
    reschedulePendingTranscription: (
      state,
      action: PayloadAction<{id: string; releaseAt: string}>
    ) => {
      state.data = state.data.map(entry =>
        (entry.id === action.payload.id ? {...entry, releaseAt: action.payload.releaseAt} : entry));
      savePendingTranscriptions(state.data);
    },
  },
});

export const {
  enqueuePendingTranscription,
  removePendingTranscription,
  reschedulePendingTranscription,
} = pendingTranscriptionsSlice.actions;

export default pendingTranscriptionsSlice.reducer;
